import React from "react"
import StatusBadge from "../assignments/StatusBadge"
import DueTimeIndicator from "../assignments/DueTimeIndicator"
import GradeDisplay from "../assignments/GradeDisplay"

// Course colors for visual identity
const courseColors = [
  "bg-violet-500",
  "bg-cyan-500",
  "bg-fuchsia-500",
  "bg-orange-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-sky-500",
  "bg-amber-500",
]

const ParentAssignmentCard = ({ assignment, showStudentName = false }) => {
  // Don't show as completed if marked missing (even if graded)
  const isMissing = assignment.submission?.missing
  const isCompleted = !isMissing && (assignment.status === "submitted" || assignment.status === "graded")

  // Get consistent color based on course ID
  const courseColor = courseColors[assignment.course.id % courseColors.length]

  return (
    <a
      href={assignment.html_url}
      target="_blank"
      rel="noopener noreferrer"
      className={`
        assignment-card block shadow-sm hover:shadow-md transition-all duration-200 p-3 sm:p-4
        ${isCompleted ? "opacity-60" : ""}
        hover-lift
      `}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Status indicator */}
        <div className={`
          mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-200
          flex items-center justify-center
          ${isCompleted
            ? "bg-emerald-500 border-emerald-500 text-white"
            : "border-[var(--cream)]"
          }
        `}>
          {isCompleted && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>

        {/* Course color indicator */}
        <div className={`w-1 self-stretch min-h-[3rem] rounded-full ${courseColor} flex-shrink-0 opacity-60`} />

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Header row with name and status */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {/* Assignment name */}
              <h3 className={`
                font-medium text-[var(--ink)] leading-snug text-sm sm:text-base
                completed-line ${isCompleted ? "is-completed text-[var(--ink-light)]" : ""}
              `}>
                {assignment.name}
              </h3>

              {/* Course name and optional student name */}
              <p className="text-xs sm:text-sm text-[var(--ink-light)] mt-0.5 truncate">
                {showStudentName && assignment.student_name && (
                  <span className="font-medium text-[var(--ink-muted)]">{assignment.student_name} &middot; </span>
                )}
                {assignment.course.name}
              </p>
            </div>

            {/* Status badge only - grade moves to meta row on mobile */}
            <div className="flex-shrink-0">
              <StatusBadge status={assignment.status} submission={assignment.submission} />
            </div>
          </div>

          {/* Meta row - wraps on mobile */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:text-sm">
            <DueTimeIndicator
              dueAt={assignment.due_at}
              urgency={assignment.urgency}
              submission={assignment.submission}
              isSubmitted={isCompleted}
            />

            {assignment.points_possible != null && (
              <span className="text-[var(--ink-light)] flex items-center gap-1">
                <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {assignment.points_possible} pts
              </span>
            )}

            {assignment.submission?.score != null && (
              <GradeDisplay
                score={assignment.submission.score}
                pointsPossible={assignment.points_possible}
                percentage={assignment.submission.percentage}
                grade={assignment.submission.grade}
              />
            )}

            {assignment.submission?.late && (
              <span className="badge badge-late">Late</span>
            )}

            {assignment.submission?.missing && (
              <span className="badge badge-missing">Missing</span>
            )}
          </div>
        </div>
      </div>
    </a>
  )
}

export default ParentAssignmentCard
